import './App.css';
import { useState, useEffect } from 'react';
import vinylData from './updated_vinyl_collection.json';

function App() {
  const [darkMode] = useState(true);
  const [expandedRecord, setExpandedRecord] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [genreFilter, setGenreFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [enlargedImage, setEnlargedImage] = useState(null);

  // Commented out price fetching functionality (not currently in use)
  // Uncomment and restore state variables if price display is needed in the future

  // Load vinyl data from JSON file
  const vinyls = vinylData;

  // Filter and sort vinyls
  const getFilteredAndSortedVinyls = () => {
    let filtered = [...vinyls];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(vinyl =>
        vinyl.artist.toLowerCase().includes(query) ||
        vinyl.title.toLowerCase().includes(query) ||
        vinyl.label.toLowerCase().includes(query) ||
        vinyl.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    // Genre filter
    if (genreFilter) {
      filtered = filtered.filter(vinyl =>
        vinyl.tags?.some(tag => tag.toLowerCase() === genreFilter.toLowerCase())
      );
    }

    // Condition filter (currently disabled in UI)
    // if (conditionFilter) {
    //   filtered = filtered.filter(vinyl => vinyl.condition === conditionFilter);
    // }

    // Year filter
    if (yearFilter) {
      filtered = filtered.filter(vinyl => vinyl.year === yearFilter);
    }

    // Sort
    switch (sortBy) {
      case 'artist-asc':
        filtered.sort((a, b) => a.artist.localeCompare(b.artist));
        break;
      case 'artist-desc':
        filtered.sort((a, b) => b.artist.localeCompare(a.artist));
        break;
      case 'price-asc':
        filtered.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
        break;
      case 'price-desc':
        filtered.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
        break;
      case 'year-asc':
        filtered.sort((a, b) => parseInt(a.year) - parseInt(b.year));
        break;
      case 'year-desc':
        filtered.sort((a, b) => parseInt(b.year) - parseInt(a.year));
        break;
      case 'newest':
      default:
        // Keep original order (newest first)
        break;
    }

    return filtered;
  };

  const filteredVinyls = getFilteredAndSortedVinyls();

  // Get available filter options based on current filters
  const getAvailableGenres = () => {
    let filtered = [...vinyls];

    // Apply year filter if active
    if (yearFilter) {
      filtered = filtered.filter(vinyl => vinyl.year === yearFilter);
    }

    const genres = new Set();
    filtered.forEach(vinyl => {
      if (vinyl.tags) {
        vinyl.tags.forEach(tag => genres.add(tag));
      }
    });

    return [...genres].sort();
  };

  const getAvailableYears = () => {
    let filtered = [...vinyls];

    // Apply genre filter if active
    if (genreFilter) {
      filtered = filtered.filter(vinyl =>
        vinyl.tags?.some(tag => tag.toLowerCase() === genreFilter.toLowerCase())
      );
    }

    const years = new Set();
    filtered.forEach(vinyl => {
      if (vinyl.year) years.add(vinyl.year);
    });

    return [...years].sort();
  };

  const availableGenres = getAvailableGenres();
  const availableYears = getAvailableYears();

  // Calculate pagination
  const totalPages = Math.ceil(filteredVinyls.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedVinyls = filteredVinyls.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, genreFilter, yearFilter, sortBy]);

  return (
    <div className={`App ${darkMode ? 'dark-mode' : ''}`}>
      <header className="header">
        <div className="logo-container">
          <img
            src={darkMode ? "/assets/feedbackloop_logo_cropped_invert_noarrow.png" : "/assets/feedbackloop_logo_cropped_noarrow.png"}
            alt="played2death"
            className="site-logo"
          />
          <span className="site-title">[played2death]</span>
        </div>
      </header>

      <main className="main">
        <div className="controls">
          <div className="controls-top">
            <input
              type="text"
              placeholder="search"
              className="search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button
              className="filter-toggle"
              onClick={() => setFiltersOpen(!filtersOpen)}
            >
              {filtersOpen ? 'hide filters' : 'show filters'}
            </button>
          </div>

          <div className={`filters-wrapper ${filtersOpen ? 'filters-open' : ''}`}>
            <div className="filters">
              <select
                className="filter-select"
                value={genreFilter}
                onChange={(e) => {
                  setGenreFilter(e.target.value);
                  setYearFilter(''); // Reset year filter when genre changes
                }}
              >
                <option value="">all genres</option>
                {availableGenres.map(genre => (
                  <option key={genre} value={genre}>{genre}</option>
                ))}
              </select>
              <select
                className="filter-select"
                value={yearFilter}
                onChange={(e) => {
                  setYearFilter(e.target.value);
                  setGenreFilter(''); // Reset genre filter when year changes
                }}
              >
                <option value="">all years</option>
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              {/* <select
                className="filter-select"
                value={conditionFilter}
                onChange={(e) => setConditionFilter(e.target.value)}
              >
                <option value="">all conditions</option>
                <option value="M">mint (M)</option>
                <option value="NM">near mint (NM)</option>
                <option value="VG+">very good plus (VG+)</option>
                <option value="VG">very good (VG)</option>
              </select> */}
            </div>
            <div className="sort">
              <select
                className="sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="newest">newest first</option>
                <option value="artist-asc">artist: a-z</option>
                <option value="artist-desc">artist: z-a</option>
                <option value="price-asc">price: low to high</option>
                <option value="price-desc">price: high to low</option>
                <option value="year-asc">year: oldest</option>
                <option value="year-desc">year: newest</option>
              </select>
            </div>
          </div>
        </div>

        <div className="vinyl-list">
          {paginatedVinyls.map(vinyl => (
            <div key={vinyl.id} className={`vinyl-item ${vinyl.swapped ? 'vinyl-item-swapped' : ''}`}>
              <div
                className={`vinyl-row ${expandedRecord === vinyl.id ? 'vinyl-row-expanded' : ''}`}
                onClick={() => !vinyl.swapped && setExpandedRecord(expandedRecord === vinyl.id ? null : vinyl.id)}
              >
                <div className="vinyl-images">
                  <div
                    className="vinyl-image-link"
                    onClick={(e) => {
                      if (expandedRecord === vinyl.id) {
                        e.stopPropagation();
                        setEnlargedImage(vinyl.imageSleeve);
                      }
                    }}
                  >
                    <img src={vinyl.imageSleeve} alt={`${vinyl.artist} - ${vinyl.title} sleeve`} className="vinyl-img" />
                  </div>
                </div>
                <div className="vinyl-main">
                  <span className="vinyl-artist">{vinyl.artist}</span>
                  <span className="vinyl-title">{vinyl.title}</span>
                  <span className="vinyl-label">{vinyl.label}</span>
                </div>
                <div className="vinyl-meta">
                  {/* <span className="vinyl-format">{vinyl.format}</span> */}
                  <span className="vinyl-year">{vinyl.year}</span>
                  <span className="vinyl-condition">{vinyl.condition}</span>
                  {/* <span className="vinyl-price">
                    {loadingPrices[vinyl.id] ? (
                      <span className="price-loading">...</span>
                    ) : prices[vinyl.id] ? (
                      <>
                        R{prices[vinyl.id]}
                        <span className="price-info-wrapper">
                          <span className="price-info-icon">[i]</span>
                          {priceStats[vinyl.id] && (
                            <span className="price-tooltip">
                              <p className="tooltip-title">lowest current listing price from discogs sellers</p>
                              <p className="tooltip-footer">{priceStats[vinyl.id].num_for_sale} currently for sale</p>
                            </span>
                          )}
                        </span>
                      </>
                    ) : (
                      <span className="price-unavailable">price unavailable</span>
                    )}
                  </span> */}
                </div>
              </div>

              {expandedRecord === vinyl.id && (
                <div className="vinyl-contact">
                  <div className="contact-content">
                    <div className="tags-and-details">
                      <div className="contact-details">
                        <span className="detail-item">{vinyl.year}</span>
                        {/* <span className="detail-item">{vinyl.condition}</span> */}
                      </div>
                      {vinyl.tags && vinyl.tags.length > 0 && (
                        <div className="vinyl-tags">
                          {vinyl.tags.map((tag, index) => (
                            <span key={index} className="tag">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    {vinyl.discogsUrl && (
                      <a href={vinyl.discogsUrl} target="_blank" rel="noopener noreferrer" className="discogs-link">view on discogs</a>
                    )}
                    {!vinyl.swapped && (
                      <>
                        <p className="contact-heading">interested in swapping?</p>
                        <div className="contact-options">
                          <a href="mailto:wianrossouw@gmail.com" className="contact-link">email</a>
                          <a href="https://instagram.com/wianrossouw" target="_blank" rel="noopener noreferrer" className="contact-link">instagram</a>
                          {/* <a href="https://discord.gg/yourinvite" target="_blank" rel="noopener noreferrer" className="contact-link">discord</a> */}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button
              className="pagination-button"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              previous
            </button>
            <span className="pagination-info">
              page {currentPage} of {totalPages}
            </span>
            <button
              className="pagination-button"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              next
            </button>
          </div>
        )}
      </main>

      {enlargedImage && (
        <div className="image-overlay" onClick={() => setEnlargedImage(null)}>
          <div className="image-overlay-content">
            <button className="close-button" onClick={() => setEnlargedImage(null)}>×</button>
            <img src={enlargedImage} alt="Enlarged vinyl sleeve" className="enlarged-image" />
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
